import DexieDB from './DexieDB';
import DexieDBProvider from './DexieDBProvider';
import { OnlineService, EventsService, ConfigurationService, AxiosService } from '.';
import { DataSet } from '@/models/dataset/DataSet';
import { CurrentDataSets } from '@/models/dataset/CurrentDataSets';
import { ApiEndpoints } from '@/config/index';
import { AppSettingsService, FilterNewService } from './index';
import { DexieDataSetEvent } from '@/models/dexie/UnsyncedDexieDataSet';
import { DataSetEventTypeEnums } from '@/enums/DataSetEventTypeEnums';
import { DataSetField, IDataSetField } from '../models/dataset/DataSetField';
import { TableDataSet } from '@/models/tableDataset/TableDataSet';
import { DataSetStore } from '../store/store.dataSet';
import { TableDataSetField, ITableDataSetField } from '../models/tableDataset/TableDataSetField';
import { FieldTypeEnum } from '@/enums/FieldTypeEnums';
import { TableDataSetActionFieldCount } from '@/models/tableDataset/TableDataSetActionFieldCount';
import { LoggerService } from '@/utils';
import { DataSetEditor } from '@/models/dataset/DataSetEditor';
import { ApiIdentifers } from '../config/index';
import { OnlineStatusEnums } from '@/enums/OnlineStatusEnums';
import { Configuration } from '@/models/configuration/Configuration';
import { LiteEvent } from '@/models/event/liteEvent';
import { AuthStore } from '@/store/store.auth';
import { DataSetBase } from '../models/dataset/DataSetBase';
import { DataSetActionFieldCount } from '@/models/dataset/DataSetActionFieldCount';
import { Helpers } from '@/helpers/Helpers';
import { ConfigurationSection } from '@/models/configuration/Section';
import { ConfigurationField } from '@/models/configuration/Field';
import { TableDataSetSection } from '../models/tableDataset/TableDataSetSection';
import { DataSetSection } from '@/models/dataset/DataSetSection';
import { UserProfileStore } from '@/store/store.userProfile';
import { DexieFilterField } from '../models/dexie/DexieFilterField';
import { DexieFilter } from '../models/dexie/DexieFilter';
import { DataSetMyMillInformation } from '@/models/dataset/DataSetMyMillInformation';

class DataSetProvider {
    private dexieDB: DexieDB = DexieDBProvider.getInstance().dexieDB;
    private readonly onUpdate = new LiteEvent<string[]>();

    private dataSets: DataSet[] = [];
    private filteredDataSets: DataSet[] = [];
    private tableDataSets: TableDataSet[] = [];

    // tslint:disable-next-line: no-empty
    constructor() {}

    /**
     * Event getter for updates
     */
    public get OnUpdate() {
        return this.onUpdate.expose();
    }

    /**
     * Get all data sets from dexie db
     */
    public async GetAllDataSetsFromDexieDB(): Promise<DataSet[]> {
        return this.dexieDB.getTable<DataSet>(this.dexieDB!.getTables().dataSet).toArray();
    }

    /**
     * Add a new DataSet
     * @param dataSet DataSet
     */
    public async AddDataSet(dataSet: DataSet): Promise<number> {
        return this.dexieDB.add<DataSetBase>(this.dexieDB!.getTables().dataSet, dataSet);
    }

    /**
     * Put/Update a DataSet
     * @param dataSet DataSet
     */
    public async PutDataSet(dataSet: DataSet): Promise<number> {
        return this.dexieDB.put<DataSetBase>(this.dexieDB!.getTables().dataSet, dataSet);
    }

    /**
     * Put/Update a DataSet
     * @param dataSet DataSet
     */
    public async bulkPutDataSet(dataSets: DataSet[]): Promise<number> {
        return this.dexieDB.bulkPut<DataSetBase>(this.dexieDB!.getTables().dataSet, dataSets);
    }

    /**
     * Delete an DataSet.
     * The dataSetId is remembered so the data set is never shown again,
     * also not if the server delivers its create/update events once more.
     * @param dataSet DataSet
     */
    public async DeleteDataSet(dataSet: DataSet): Promise<void> {
        AppSettingsService.AddDeletedDataSetId(dataSet.dataSetId);
        this.removeDataSetFromMemory(dataSet.dataSetId);
        return this.dexieDB.del<DataSetBase>(this.dexieDB!.getTables().dataSet, dataSet.id);
    }

    /**
     * Method to remove a data set from all in-memory lists
     * @param inDataSetId   The dataSetId (TabID) of the deleted data set
     */
    private removeDataSetFromMemory(inDataSetId?: string) {
        if (inDataSetId == null) {
            return;
        }

        this.removeByDataSetId(this.dataSets, inDataSetId);
        this.removeByDataSetId(this.filteredDataSets, inDataSetId);
        this.removeByDataSetId(this.tableDataSets, inDataSetId);
    }

    private removeByDataSetId(inList: Array<{ dataSetId?: string }>, inDataSetId: string) {
        for (let index = inList.length - 1; index >= 0; index--) {
            if (inList[index].dataSetId === inDataSetId) {
                inList.splice(index, 1);
            }
        }
    }

    /**
     * Method to clear all data sets.
     * Set length of array to 0 to delete all data from memory
     */
    public ClearAllDataSets() {
        this.dataSets.splice(0, this.dataSets.length);
    }

    /**
     * Method to clear all table data sets.
     * Set length of array to 0 to delete all data from memory
     */
    public ClearAllTableDataSets() {
        this.tableDataSets = [];
        // for (let index = this.tableDataSets.length - 1; index >= 0; index--) {
        //     this.tableDataSets.pop();
        // }
    }

    public ClearAllFilteredDataSets() {
        this.filteredDataSets = [];
    }

    /**
     * Build filteredDataSets for the logged-in user's datasets (Created by me).
     * Fallback to "all datasets" if user profile is incomplete to avoid empty screen.
     */
    private async GetLoggedInUserDataSets(): Promise<DataSet[]> {
        const profile = UserProfileStore.GetUserProfile;

        // Avoid optional chaining / nullish coalescing
        const userEmail = (profile && profile.userEmail ? profile.userEmail : '').trim();
        const userName = (profile && profile.userName ? profile.userName : '').trim();
        const userId = profile && profile.userId ? profile.userId : undefined;

        this.ClearAllFilteredDataSets();

        // If user identity is available, filter by me; else show all.
        var source: DataSet[] = this.dataSets;
        if (userId || userEmail || userName) {
            source = this.dataSets.filter(function(ds) {
                return (
                    (userId && ds.created.userId === userId) ||
                    (userEmail && ds.created.userEmail && ds.created.userEmail.toLowerCase().includes(userEmail.toLowerCase())) ||
                    (userName && ds.created.userName && ds.created.userName.toLowerCase() === userName.toLowerCase())
                );
            });
        }

        this.filteredDataSets = source.map(function(ds) {
            return new DataSet(ds);
        });

        this.sortByCreationDate(this.filteredDataSets);
        this.filteredDataSets.reverse();

        return this.filteredDataSets;
    }

    public async GetAllDataSetsForFilters(): Promise<DataSet[]> {
        const selectedFilter = FilterNewService.UsedFilterForDataSets;

        let temp: DataSet[] = [];
        const orFiltered: DataSet[] = [];
        this.ClearAllFilteredDataSets();

        if (selectedFilter !== undefined && selectedFilter.data.length >= 0) {
            selectedFilter.data.forEach(filterField => {
                temp = [];
                switch (filterField.identifier) {
                    case ApiIdentifers.filterCreatedAt:
                        temp = this.dataSets.filter(ds => this.compareDates(ds.created.date, filterField.valueString || '') === 0);
                        break;
                    case ApiIdentifers.filterCreatedBy:
                        temp = this.dataSets.filter(
                            ds =>
                                ds.created.userEmail.toLowerCase().includes((filterField.valueString || '').toLowerCase()) ||
                                ds.created.userName.toLowerCase().includes((filterField.valueString || '').toLowerCase())
                        );
                        break;
                    case ApiIdentifers.filterEditedAt:
                        temp = this.dataSets.filter(ds => ds.lastModified && this.compareDates(ds.lastModified.date, filterField.valueString || '') === 0);
                        break;
                    case ApiIdentifers.filterEditedBy:
                        temp = this.dataSets.filter(
                            ds =>
                                ds.lastModified &&
                                (ds.lastModified.userEmail.toLowerCase().includes((filterField.valueString || '').toLowerCase()) ||
                                    ds.lastModified.userName.toLowerCase().includes((filterField.valueString || '').toLowerCase()))
                        );
                        break;
                    case ApiIdentifers.filterDatasetsByMe:
                        let userEmail = selectedFilter.createdBy;
                        const userName = UserProfileStore.GetUserProfile.userName;
                        const userId = UserProfileStore.GetUserProfile.userId;
                        if (userEmail === '') {
                            userEmail = UserProfileStore.GetUserProfile.userEmail || '';
                        }
                        if (userId != null || userEmail !== '') {
                            temp = this.dataSets.filter(
                                ds =>
                                    ds.created.userId === userId ||
                                    ds.created.userEmail.toLowerCase().includes(userEmail.toLowerCase()) ||
                                    ds.created.userName.toLowerCase() === (userName || '').toLowerCase()
                            );
                        }
                        break;
                    case ApiIdentifers.filterTabId:
                        temp = this.dataSets.filter(ds => ds.dataSetId === filterField.valueString);
                        break;
                    default:
                        temp = this.filterSectionByField(this.dataSets, filterField);
                        break;
                }

                // avoid double adding
                temp.forEach(tds => {
                    if (orFiltered.some(fds => fds.dataSetId === tds.dataSetId) === false) {
                        orFiltered.push(tds);
                    }
                });
            });

            /**
             * check for each ds if it has alle selected fitlers inside
             * to have a AND wise fitler
             */
            this.filteredDataSets = this.filterAndWise(orFiltered, selectedFilter);
        } else {
            this.dataSets.forEach(ds => {
                this.filteredDataSets.push(new DataSet(ds));
            });
        }
        this.sortByCreationDate(this.filteredDataSets);
        this.filteredDataSets.reverse();
        return this.filteredDataSets;
    }

    public GetAllDataSets(): DataSet[] {
        return this.dataSets;
    }

    public get GetTableDataSets(): TableDataSet[] {
        return this.tableDataSets;
    }

    public get GetFilteredDataSets(): DataSet[] {
        return this.filteredDataSets;
    }

    /**
     * Method to fetch al data sets and trigger update if neded
     */
    public async FetchDataSets(): Promise<string[]> {
        return new Promise(async (resolve, reject) => {
            const appSettings = AppSettingsService.GetAppSettings;
            const snapshotFetched: Date = new Date((appSettings && appSettings.snapshotFetched) || 'undefined');
            let dataSets: DataSet[] = [];
            let newDB = false;
            let updatesDone: string[] = [];
            // fetch initial snapshot
            if (appSettings.firstStart && appSettings.snapshotFetched === undefined && isNaN(snapshotFetched.getTime())) {
                newDB = true;
                await this.getCurrentSnapshot()
                    .then(async (data: any) => {
                        const current = new CurrentDataSets(data);
                        dataSets = current.dataSets;
                        AppSettingsService.SetSnapshotFetched(new Date().toISOString());
                        AppSettingsService.SetLastEventId(current.lastEventId);
                        AppSettingsService.SetFirstStart(false);
                        /**
                         * For new fetched snapshots this should be fine
                         * so we can set it already to true
                         */
                        AppSettingsService.SetTBlenMatrialUuidChanged(true);
                        await AppSettingsService.PutAppSettings(appSettings).catch(error => {
                            LoggerService.LogError(`Error on save app settings: `, error);
                        });
                    })
                    .catch((error: any) => {
                        LoggerService.LogError(`Error on sync dat sets: `, error);
                        reject(error);
                    });
            } else if (appSettings.tBlenMatrialUuidChanged == null || appSettings.tBlenMatrialUuidChanged === false) {
                /**
                 * Check all local saved datasets and update
                 */
                await this.fixTBlenMaterialComponentUuid();
            }

            if (newDB === true) {
                await this.bulkPutDataSet(dataSets).catch(error => {
                    LoggerService.LogError(`Error on bulk put data sets: `, error);
                });
            }

            if (OnlineService.GetOnlineStatus === OnlineStatusEnums.ONLINE && AuthStore.GetIsLoggedIn === true) {
                // Update only if online
                await EventsService.SyncEventsFromServer(appSettings.lastEventId)
                    .then(async eventsData => {
                        updatesDone = await this.updateDataSets(eventsData);
                    })
                    .catch(error => {
                        LoggerService.LogError(`Error on update data sets from events: `, error);
                    });
            }

            await this.setDataSets();

            resolve(updatesDone);
        });
    }

    /**
     * Method to fetch table data sets
     *
     */
    public async FetchTableDataSets(): Promise<TableDataSet[]> {
        await this.GetAllDataSetsForFilters();

        const self = this;

        return new Promise(async (resolve, reject) => {
            self.tableDataSets.splice(0, this.tableDataSets.length);
            if (self.filteredDataSets && self.filteredDataSets.length > 0) {
                self.filteredDataSets.forEach(dataSet => {
                    self.tableDataSets.push(self.GenerateTableDataSet(dataSet));
                });
                self.FetchMaximumActionCounts();
                resolve(self.tableDataSets);
            } else {
                reject('filteredDataSets empty');
            }
        });
    }

    /**
     * Build filteredDataSets for table view.
     * - useFilter === true  => apply FilterNewService.UsedFilterForDataSets across ALL datasets
     * - useFilter === false => show only datasets created by the logged-in user
     */
    public async FetchFilteredDataSets(useFilter?: boolean): Promise<DataSet[]> {
        if (useFilter === true) {
            await this.GetAllDataSetsForFilters();
        } else {
            await this.GetLoggedInUserDataSets();
        }

        const self = this;
        return new Promise(async resolve => {
            // Keep your existing max-count aggregation for table headers/cards
            self.FetchMaximumActionCountsNew();
            resolve(self.filteredDataSets);
        });
    }

    public UpdateFilteredDataSets(inDataSetIds?: string[]) {
        if (inDataSetIds && inDataSetIds.length > 0) {
            for (const dsId of inDataSetIds) {
                const changedDataSet = this.dataSets.find(ds => ds.dataSetId === dsId);
                const changedFilteredDataSet = this.filteredDataSets.find(ds => ds.dataSetId === dsId);
                if (changedDataSet !== undefined) {
                    if (changedFilteredDataSet !== undefined) {
                        const index = this.filteredDataSets.map(tds => tds.dataSetId).indexOf(changedFilteredDataSet.dataSetId);
                        if (index >= 0) {
                            // update
                            this.filteredDataSets[index] = new DataSet(changedDataSet);
                        } else {
                            // create
                            this.filteredDataSets.push(new DataSet(changedDataSet));
                        }
                    }
                } else {
                    LoggerService.LogWarn(`could not find updated dataSet: ${dsId}`);
                }
            }
            this.sortByCreationDate(this.filteredDataSets);
            this.filteredDataSets.reverse();
        }
    }

    public async FetchMaximumActionCounts() {
        const actionCounts: TableDataSetActionFieldCount[] = [];
        this.tableDataSets.forEach(dataSet => {
            if (dataSet.actionItems !== undefined) {
                dataSet.actionItems.forEach(item => {
                    const actionCount = actionCounts.find(ac => ac.identifier === item.identifier);
                    if (actionCount === undefined) {
                        actionCounts.push(new TableDataSetActionFieldCount(item));
                    } else if (item.fieldCount > actionCount.fieldCount) {
                        actionCount.fieldCount = item.fieldCount;
                    }
                });
            }
        });

        await DataSetStore.FetchMaxActionCounts(actionCounts)
            .then(data => {
                LoggerService.LogDebug(`FetchMaxActionCounts: `, data);
            })
            .catch(error => {
                LoggerService.LogError(`Error on FetchMaxActionCounts: `, error);
            });
    }

    public async FetchMaximumActionCountsNew() {
        const actionCounts: TableDataSetActionFieldCount[] = [];

        this.filteredDataSets.forEach(dataSet => {
            dataSet.sections.forEach(section => {
                let fieldCount = 0;
                switch (section.identifier) {
                    case ApiIdentifers.material:
                    case ApiIdentifers.contacts:
                    case ApiIdentifers.sample:
                        fieldCount = section.fields.length;
                        break;
                    default:
                        break;
                }

                const actionCount = actionCounts.find(ac => ac.identifier === section.identifier);
                if (actionCount === undefined) {
                    actionCounts.push(
                        new TableDataSetActionFieldCount({
                            name: '',
                            identifier: section.identifier,
                            fieldCount
                        })
                    );
                } else if (fieldCount > actionCount.fieldCount) {
                    actionCount.fieldCount = fieldCount;
                }
            });
        });

        await DataSetStore.FetchMaxActionCounts(actionCounts)
            .then(data => {
                LoggerService.LogDebug(`FetchMaxActionCounts: `, data);
            })
            .catch(error => {
                LoggerService.LogError(`Error on FetchMaxActionCounts: `, error);
            });
    }

    public GenerateTableDataSet(inDataSet: DataSet): TableDataSet {
        const machineSections = new Configuration(ConfigurationService.GetMachinesConfiguration).sections;
        const allSections: ConfigurationSection[] = [];
        ConfigurationService.GetCompleteConfigurationSections.forEach(sec => {
            // just add copies of the sections to avoid unwanted mutations
            allSections.push(new ConfigurationSection(sec));
        });

        const tableDataSet = new TableDataSet({ sections: allSections });
        tableDataSet.dataSetId = inDataSet.dataSetId;
        tableDataSet.id = inDataSet.id;
        tableDataSet.unsyncedParts = inDataSet.unsyncedParts === true;
        tableDataSet.created = new DataSetEditor(inDataSet.created);
        if (inDataSet.lastModified != null) {
            tableDataSet.lastModified = new DataSetEditor(inDataSet.lastModified);
        }
        if (inDataSet.myMillImport != null) {
            tableDataSet.myMillImport = new DataSetMyMillInformation(inDataSet.myMillImport);
        }

        const remainingActionSections: string[] = allSections.filter(sec => sec.action != null).map(sec => sec.identifier);

        inDataSet.sections.forEach(inSection => {
            const tableSection = tableDataSet.sections.find(sec => sec.identifier === inSection.identifier);
            if (tableSection !== undefined) {
                if (tableSection.action !== undefined) {
                    /*
                     * In case we have an action, we need to put the fields inside a new field
                     */
                    this.convertActionSection(tableSection, inSection);
                    // const fields = tableSection.fields;
                    // tableSection.fields = [];
                    // const field = new TableDataSetField();
                    // field.position = 0;
                    // field.name = tableSection.name;
                    // field.identifier = `${tableSection.identifier}__${field.position}`;
                    // field.type = FieldTypeEnum.ACTION_SUBFIELD;
                    // field.fields = [];
                    // field.action = tableSection.action;
                    // field.required = fields[0].required;
                    // tableSection.fields.push(field);
                    // fields.forEach((x: TableDataSetField) => {
                    //     field.fields!.push(new TableDataSetField(x));
                    // });
                    // for (let index = 1; index < inSection.fields.length; index++) {
                    //     const element = inSection.fields[index];
                    //     const clone = new TableDataSetField(field);
                    //     clone.identifier = element.identifier.replace(/__[0-9]+/, `__${index}`);
                    //     tableSection.fields.push(clone);
                    // }

                    if (tableDataSet.actionItems === undefined) {
                        tableDataSet.actionItems = [];
                    }
                    tableDataSet.actionItems.push(
                        new TableDataSetActionFieldCount({
                            identifier: tableSection.identifier,
                            name: tableSection.name,
                            fieldCount: tableSection.fields.length
                        })
                    );

                    remainingActionSections.splice(
                        remainingActionSections.findIndex(id => id === tableSection.identifier),
                        1
                    );
                }
                tableSection.fields.forEach((tsField: ITableDataSetField) => {
                    const dsField = inSection.fields.find(field => field.identifier === tsField.identifier);
                    if (dsField !== undefined) {
                        this.CheckSubField(dsField, tsField);
                    }
                });
            }
        });

        // in case we have an action section that was not in the dataSet, except samples
        for (const id of remainingActionSections) {
            const actionSection = tableDataSet.sections.find(sec => sec.identifier !== ApiIdentifers.sample && sec.identifier === id);
            if (actionSection != null) {
                this.convertActionSection(actionSection);
            }
        }

        const machineOrder = tableDataSet.sections.find(sec => sec.identifier === ApiIdentifers.machineOrder);
        if (machineOrder !== undefined) {
            machineSections.forEach(ms => {
                if (inDataSet.sections.some(sec => sec.identifier === ms.identifier)) {
                    const mo = machineOrder.fields.find(field => field.identifier === ms.identifier);
                    if (mo !== undefined) {
                        mo.valueBool = true;
                    }
                }
            });
        }

        return tableDataSet;
    }

    public GenerateTableDataSetEmpty(): TableDataSet {
        const allSections: ConfigurationSection[] = [];
        ConfigurationService.GetCompleteConfigurationSections.forEach(sec => {
            // just add copies of the sections to avoid unwanted mutations
            allSections.push(new ConfigurationSection(sec));
        });

        const tableDataSet = new TableDataSet({ sections: allSections });

        tableDataSet.sections.forEach(inSection => {
            if (inSection !== undefined) {
                if (inSection.action !== undefined) {
                    /*
                     * In case we have an action, we need to put the fields inside a new field
                     */
                    this.convertActionSection(inSection, inSection);

                    if (tableDataSet.actionItems === undefined) {
                        tableDataSet.actionItems = [];
                    }
                    tableDataSet.actionItems.push(
                        new TableDataSetActionFieldCount({
                            identifier: inSection.identifier,
                            name: inSection.name,
                            fieldCount: inSection.fields.length
                        })
                    );
                }
            }
        });

        return tableDataSet;
    }

    public TriggerUpdateEvent(inIds: string[]) {
        this.onUpdate.trigger(inIds);
    }

    /**
     * Deep-clones a dataset for sync baselines (before optimistic local updates).
     */
    public CloneDataSet(dataSet: DataSet): DataSet {
        return new DataSet(JSON.parse(JSON.stringify(dataSet)));
    }

    /**
     * Keeps IndexedDB in sync with the editor immediately after save (before background sync).
     */
    public async ApplyLocalDataSetUpdate(savedDataSet: DataSet): Promise<void> {
        if (savedDataSet.id == null) {
            return;
        }

        const localDataSet = this.dataSets.find(ds => ds.id === savedDataSet.id);
        if (localDataSet === undefined) {
            return;
        }

        localDataSet.unsyncedParts = true;

        for (const inSection of savedDataSet.sections) {
            const localSection = localDataSet.sections.find(sec => sec.identifier === inSection.identifier);
            if (localSection === undefined) {
                localDataSet.sections.push(new DataSetSection(inSection));
                continue;
            }

            if (inSection.identifier === ApiIdentifers.material) {
                this.mergeMaterialSectionFields(localSection.fields, inSection.fields);
            } else {
                this.updateFields(localSection.fields, inSection.fields);
            }
        }

        try {
            await this.PutDataSet(localDataSet);
            if (localDataSet.dataSetId) {
                this.TriggerUpdateEvent([localDataSet.dataSetId]);
            }
        } catch (error) {
            LoggerService.LogError(`Error applying local data set update for id ${savedDataSet.id}: `, error);
        }
    }

    private convertActionSection(inSection: TableDataSetSection, inDataSection?: DataSetSection) {
        const fields = inSection.fields;
        inSection.fields = [];
        const field = new TableDataSetField();
        field.position = 0;
        field.name = inSection.name;
        field.identifier = `${inSection.identifier}__${field.position}`;
        field.type = FieldTypeEnum.ACTION_SUBFIELD;
        field.fields = [];
        field.action = inSection.action;
        field.required = fields[0].required;
        inSection.fields.push(field);
        fields.forEach((x: TableDataSetField) => {
            field.fields!.push(new TableDataSetField(x));
        });
        if (inDataSection != null) {
            for (let index = 1; index < inDataSection.fields.length; index++) {
                const element = inDataSection.fields[index];
                const clone = new TableDataSetField(field);
                clone.identifier = element.identifier.replace(/__[0-9]+/, `__${index}`);
                inSection.fields.push(clone);
            }
        }
    }

    private async setDataSets() {
        return new Promise<void>((resolve, reject) => {
            this.GetAllDataSetsFromDexieDB()
                .then(data => {
                    if (data && data.length > 0) {
                        this.dataSets.splice(0, this.dataSets.length);
                        data.forEach(ds => {
                            if (AppSettingsService.IsDataSetDeleted(ds.dataSetId)) {
                                // deleted data sets must never show up again
                                this.purgeDeletedDataSet(ds.dataSetId || '');
                                return;
                            }
                            this.dataSets.push(new DataSet(ds));
                        });
                        this.setDataSetActionsCounts();
                    }
                    resolve();
                })
                .catch(error => {
                    LoggerService.LogError(`Error on set data sets: `, error);
                    reject(error);
                });
        });
    }

    private setDataSetActionsCounts() {
        this.dataSets.forEach(dataSet => {
            dataSet.sections.forEach(sec => {
                const actionCount = new DataSetActionFieldCount();
                switch (sec.identifier) {
                    case ApiIdentifers.material:
                        actionCount.identifier = ApiIdentifers.material;
                        actionCount.fieldCount = sec.fields.length;
                        break;
                    case ApiIdentifers.contacts:
                        actionCount.identifier = ApiIdentifers.contacts;
                        actionCount.fieldCount = sec.fields.length;
                        break;
                    case ApiIdentifers.sample:
                        actionCount.identifier = ApiIdentifers.sample;
                        actionCount.fieldCount = sec.fields.length;
                        break;

                    default:
                        break;
                }
                if (actionCount.identifier !== '') {
                    if (dataSet.actionItems === undefined) {
                        dataSet.actionItems = [];
                    }
                    dataSet.actionItems.push(actionCount);
                }
            });
        });
    }

    private CheckSubField(inDSField: IDataSetField, inTSField: ITableDataSetField, doNotCheckFields: boolean = false) {
        if (inDSField.fields !== undefined && !doNotCheckFields) {
            if (inDSField.fields.length === 0 && (inDSField.valueBool !== undefined || inDSField.valueNumber != null || inDSField.valueString !== undefined)) {
                // fallback if fields is set to [] by false, seems to be a buggy sometimes
                this.CheckSubField(inDSField, inTSField, true);
            } else {
                inDSField.fields.forEach(subDSField => {
                    const subTSField = inTSField.fields && inTSField.fields.find(field => field.identifier === subDSField.identifier);
                    if (subTSField !== undefined) {
                        this.CheckSubField(subDSField, subTSField);
                    }
                });
            }
        } else {
            inTSField.valueBool = inDSField.valueBool;
            inTSField.valueNumber = inDSField.valueNumber;
            inTSField.valueString = inDSField.valueString;
            inTSField.changed = inDSField.changed;
        }
    }

    private async getCurrentSnapshot(): Promise<CurrentDataSets> {
        return new Promise(async (resolve, reject) => {
            if (OnlineService.GetOnlineStatus === OnlineStatusEnums.ONLINE && AuthStore.GetIsLoggedIn === true) {
                await AxiosService.get(ApiEndpoints.eventsCurrent, null)
                    .then((response: any) => {
                        if (response.data) {
                            resolve(new CurrentDataSets(response.data));
                        }
                    })
                    .catch((error: any) => {
                        reject(error);
                    });
            } else {
                reject(`Can't get current snapshot, not online or logged in`);
            }
        });
    }

    private async updateDataSets(inEvents: DexieDataSetEvent[]): Promise<string[]> {
        const updateDataSetIds: string[] = [];
        if (inEvents.length === 0) {
            LoggerService.LogDebug(`No events to update`);
            return updateDataSetIds;
        }

        const dataSetTable = this.dexieDB.getTable<DataSet>(this.dexieDB.getTables().dataSet);
        if (dataSetTable === undefined) {
            LoggerService.LogError(`Cant open data set table`);
            return updateDataSetIds;
        }

        /**
         * Check incomming events for old/wrong identifier of
         * tBlendMaterialComponent and change it.
         * See also: https://appsfactory.atlassian.net/browse/TTDB-874
         */
        inEvents.forEach(ie => {
            if (ie.dataSet) {
                const sectionBlowRoom = ie.dataSet.sections.find(s => s.identifier === ApiIdentifers.blowRoom);
                if (sectionBlowRoom) {
                    const tBlend = sectionBlowRoom.fields.find(f => f.identifier === ApiIdentifers.tBlend);
                    if (tBlend && tBlend.fields) {
                        const feeder = tBlend.fields.find(tf => tf.identifier === ApiIdentifers.BLBOBLHFBLPF);
                        if (feeder && feeder.fields) {
                            const materialComponent = feeder.fields.find(ff => ff.identifier === ApiIdentifers.materialComponent);
                            if (materialComponent) {
                                materialComponent.identifier = ApiIdentifers.tBlendMaterialComponent;
                                LoggerService.debug(`Change tBlendMaterialComponent identifier for an event: `, ie);
                            }
                        }
                    }
                }
            }
        });

        let lastEventId = inEvents[0].id;
        for (const inEvent of inEvents) {
            if (inEvent.dataSet.dataSetId === undefined) {
                continue;
            }

            /**
             * Never bring back a deleted data set. Older create/update events
             * for it can be delivered again, e.g. when an event range is
             * fetched a second time.
             */
            if (AppSettingsService.IsDataSetDeleted(inEvent.dataSet.dataSetId)) {
                lastEventId = inEvent.id;
                await this.purgeDeletedDataSet(inEvent.dataSet.dataSetId);
                continue;
            }

            let dataSet: DataSet | undefined;
            try {
                dataSet = await dataSetTable
                    .where('dataSetId')
                    .equals(inEvent.dataSet.dataSetId)
                    .first();
            } catch (error) {
                LoggerService.LogError(`Error on fetching data set with id ${inEvent.dataSet.dataSetId}: `, error);
                continue;
            }

            if (dataSet !== undefined && inEvent.event === DataSetEventTypeEnums.UPDATE) {
                if (inEvent.user !== undefined) {
                    dataSet.lastModified = new DataSetEditor(inEvent.user);
                } else {
                    dataSet.lastModified = new DataSetEditor({
                        date: new Date().toISOString(),
                        userName: 'unknown',
                        userId: '0',
                        userEmail: 'unknown'
                    });
                }
                for (const inSection of inEvent.dataSet.sections) {
                    const orginalSection = dataSet.sections.find(sec => sec.identifier === inSection.identifier);
                    if (orginalSection !== undefined) {
                        if (inSection.identifier === ApiIdentifers.material) {
                            this.mergeMaterialSectionFields(orginalSection.fields, inSection.fields);
                        } else {
                            this.updateFields(orginalSection.fields, inSection.fields);
                        }
                    } else {
                        dataSet.sections.push(inSection);
                    }
                }
                try {
                    await this.PutDataSet(dataSet);
                    lastEventId = inEvent.id;
                    updateDataSetIds.push(dataSet.dataSetId || '');
                } catch (error) {
                    LoggerService.LogError(`Error on put updated data set with id ${inEvent.dataSet.dataSetId}: `, error);
                }
            } else if (inEvent.event === DataSetEventTypeEnums.DELETE) {
                try {
                    if (dataSet !== undefined) {
                        await this.DeleteDataSet(dataSet);
                        LoggerService.LogDebug(`DataSet ${dataSet.dataSetId} deleted.`);
                    } else {
                        // data set is already gone locally, just remember the deletion
                        AppSettingsService.AddDeletedDataSetId(inEvent.dataSet.dataSetId);
                    }
                    lastEventId = inEvent.id;
                    updateDataSetIds.push(inEvent.dataSet.dataSetId || '');
                } catch (error) {
                    LoggerService.LogError(`Error on delete data set with id ${inEvent.dataSet.dataSetId}: `, error);
                }
            } else if (dataSet === undefined && inEvent.event === DataSetEventTypeEnums.CREATE) {
                if (inEvent.user !== undefined) {
                    inEvent.dataSet.created = new DataSetEditor(inEvent.user);
                }
                try {
                    await this.AddDataSet(inEvent.dataSet as DataSet);
                    lastEventId = inEvent.id;
                    updateDataSetIds.push(inEvent.dataSet.dataSetId || '');
                } catch (addError) {
                    LoggerService.LogError(`Error on create dataset from event: `, addError);
                }
            } else if (dataSet !== undefined && inEvent.event === DataSetEventTypeEnums.CREATE) {
                lastEventId = inEvent.id;
                LoggerService.LogWarn(`DataSet with dataSetId: ${inEvent.dataSet.dataSetId} already exist, ignoring`);
            } else {
                LoggerService.LogDebug(`DataSet for event ${inEvent.id} is empty`);
            }
        }
        /**
         * Only move the pointer forward. Moving it back makes the same event
         * range be fetched and applied again on every following sync.
         */
        const knownLastEventId = AppSettingsService.GetAppSettings.lastEventId || 0;
        if (lastEventId != null && lastEventId > knownLastEventId) {
            AppSettingsService.ChangeLastEventId(lastEventId);
        }
        return updateDataSetIds;
    }

    /**
     * Method to make sure a deleted data set is not stored locally anymore
     * @param inDataSetId   The dataSetId (TabID) of the deleted data set
     */
    private async purgeDeletedDataSet(inDataSetId: string): Promise<void> {
        const dataSetTable = this.dexieDB.getTable<DataSet>(this.dexieDB.getTables().dataSet);
        if (dataSetTable === undefined) {
            return;
        }

        try {
            const dataSet = await dataSetTable
                .where('dataSetId')
                .equals(inDataSetId)
                .first();
            if (dataSet !== undefined && dataSet.id != null) {
                await this.dexieDB.del<DataSetBase>(this.dexieDB.getTables().dataSet, dataSet.id);
                LoggerService.LogDebug(`Ignored event for deleted data set ${inDataSetId}`);
            }
        } catch (error) {
            LoggerService.LogError(`Error on purging deleted data set ${inDataSetId}: `, error);
        }

        this.removeDataSetFromMemory(inDataSetId);
    }

    private mergeMaterialSectionFields(originalFields: DataSetField[], newFields: DataSetField[]): void {
        const prefix = `${ApiIdentifers.material}__`;
        const newSubsections = this.dedupeFieldsByIdentifier(
            newFields.filter(f => f.identifier != null && f.identifier.indexOf(prefix) === 0)
        );
        const otherNewFields = newFields.filter(f => f.identifier == null || f.identifier.indexOf(prefix) !== 0);

        if (newSubsections.length === 0) {
            this.updateFields(originalFields, newFields);
            return;
        }

        const oldSubsections = originalFields.filter(f => f.identifier != null && f.identifier.indexOf(prefix) === 0);
        if (this.shouldReplaceMaterialSubsections(oldSubsections, newSubsections)) {
            const newIds = new Set(newSubsections.map(f => f.identifier));
            for (let i = originalFields.length - 1; i >= 0; i--) {
                const id = originalFields[i].identifier;
                if (id != null && id.indexOf(prefix) === 0 && !newIds.has(id)) {
                    originalFields.splice(i, 1);
                }
            }
            for (const newField of newSubsections) {
                const existing = originalFields.find(f => f.identifier === newField.identifier);
                if (existing !== undefined) {
                    if (newField.fields && existing.fields) {
                        this.updateFields(existing.fields, newField.fields);
                    } else if (newField.fields) {
                        existing.fields = newField.fields.map(f => new DataSetField(f));
                    }
                } else {
                    originalFields.push(new DataSetField(newField));
                }
            }
        } else {
            this.updateFields(originalFields, newSubsections);
        }

        if (otherNewFields.length > 0) {
            this.updateFields(originalFields, otherNewFields);
        }
    }

    private shouldReplaceMaterialSubsections(oldSubsections: DataSetField[], newSubsections: DataSetField[]): boolean {
        if (newSubsections.length < oldSubsections.length) {
            return true;
        }
        const newIds = new Set(newSubsections.map(f => f.identifier));
        for (const oldField of oldSubsections) {
            if (oldField.identifier != null && !newIds.has(oldField.identifier)) {
                return true;
            }
        }
        return false;
    }

    private dedupeFieldsByIdentifier(fields: DataSetField[]): DataSetField[] {
        const seen = new Set<string>();
        const result: DataSetField[] = [];
        for (const field of fields) {
            if (!field.identifier || seen.has(field.identifier)) {
                continue;
            }
            seen.add(field.identifier);
            result.push(field);
        }
        return result;
    }

    private updateFields(originalFields: DataSetField[], newFields: DataSetField[], changed: boolean = true) {
        for (const newField of newFields) {
            const originalField = originalFields.find(field => field.identifier === newField.identifier);
            if (originalField !== undefined) {
                if (newField.fields && newField.fields.length === 0 && (newField.valueBool !== undefined || newField.valueNumber != null || newField.valueString !== undefined)) {
                    // fallback if fields is set to [] by false, seems to be a buggy sometimes
                    originalField.changed = changed;
                    originalField.valueBool = newField.valueBool;
                    originalField.valueNumber = newField.valueNumber;
                    originalField.valueString = newField.valueString;
                } else if (newField.fields) {
                    // update field
                    if (originalField.fields) {
                        this.updateFields(originalField.fields, newField.fields);
                    } else {
                        originalField.fields = newField.fields;
                    }
                } else if (!('valueNumber' in newField || 'valueString' in newField || 'valueBool' in newField)) {
                    originalFields.splice(originalFields.indexOf(originalField), 1);
                } else {
                    originalField.changed = changed;
                    originalField.valueBool = newField.valueBool;
                    originalField.valueNumber = newField.valueNumber;
                    originalField.valueString = newField.valueString;
                }
            } else {
                // add field
                originalFields.push(newField);
            }
        }
    }

    /**
     * Methods to sort by creation or last modifed date
     * If both have a lastModified modified value then compare these two dates,
     * else if only one had a lastModified the this one is newer.
     * In any other case use the creation date
     * @param inDataSets DataSet to sort
     */
    private sortByCreationDate(inDataSets: DataSet[]) {
        inDataSets.sort((lhs, rhs) => {
            let ret = 0;
            if (lhs.lastModified !== undefined && rhs.lastModified !== undefined) {
                ret = Helpers.compareDateStrings(lhs.lastModified.date, rhs.lastModified.date);
            } else if (lhs.lastModified !== undefined && rhs.lastModified === undefined) {
                ret = Helpers.compareDateStrings(lhs.lastModified.date, rhs.created.date);
            } else if (lhs.lastModified === undefined && rhs.lastModified !== undefined) {
                ret = Helpers.compareDateStrings(lhs.created.date, rhs.lastModified.date);
            } else {
                Helpers.compareDateStrings(lhs.created.date, rhs.created.date);
            }
            return ret;
        });
    }

    private getConfigureValue(identifier: string): ConfigurationField | null {
        let configureMinMax = null;
        const sections = ConfigurationService.GetCompleteConfigurationSections;
        if (sections !== null) {
            // TODO: change to for-of-loop?
            // tslint:disable-next-line:prefer-for-of
            for (let index = 0; index < sections.length; index++) {
                const section = sections[index];
                if (section.fields !== undefined && section.fields.length > 0) {
                    configureMinMax = this.getSubConfigurationValue(identifier, section.fields);
                    if (configureMinMax !== null) {
                        break;
                    }
                }
            }
        }
        return configureMinMax;
    }

    private getSubConfigurationValue(identifier: string, fields: ConfigurationField[]): ConfigurationField | null {
        let element = null;
        // TODO: change to for-of-loop?
        // tslint:disable-next-line:prefer-for-of
        for (let index = 0; index < fields.length; index++) {
            element = fields[index];
            if (element.identifier === identifier) {
                break;
            } else if (element.fields !== undefined && element.fields.length > 0) {
                element = this.getSubConfigurationValue(identifier, element.fields);
                if (element !== null) {
                    break;
                }
            } else {
                element = null;
            }
        }
        return element;
    }

    /**
     * Compare two date iso strings without time.
     *
     * @param lhs
     * @param rhs
     * @returns  Zero if equals, -1 if lhs is lower than rhs, else 1
     */
    private compareDates(lhs: string, rhs: string): number {
        const lhsDate = new Date(lhs);
        const rhsDate = new Date(rhs);
        lhsDate.setHours(0, 0, 0, 0);
        rhsDate.setHours(0, 0, 0, 0);

        if (lhsDate.getTime() === rhsDate.getTime()) {
            // equals ==> 0
            return 0;
        } else if (lhsDate.getTime() < rhsDate.getTime()) {
            // lhs is lower than rhs ==> -1
            return -1;
        } else if (lhsDate.getTime() > rhsDate.getTime()) {
            // lhs is greater than rhs ==> 1
            return 1;
        }

        return -1;
    }

    private filterSectionByField(inDataSets: DataSet[], inFilterField: DexieFilterField): DataSet[] {
        const filtered: DataSet[] = [];

        for (const dataSet of inDataSets) {
            for (const section of dataSet.sections) {
                let found = false;
                for (const field of section.fields) {
                    found = this.filterSectionByFieldCheckField(field, inFilterField);
                    if (found) {
                        filtered.push(dataSet);
                        break;
                    }
                }
                if (found) {
                    break;
                }
            }
        }

        return filtered;
    }

    private filterSectionByFieldCheckField(inField: DataSetField, inFilterField: DexieFilterField): boolean {
        let found = false;
        if (inField.fields != null) {
            for (const subField of inField.fields) {
                found = this.filterSectionByFieldCheckField(subField, inFilterField);
                if (found) {
                    break;
                }
            }
        } else {
            if (inField.identifier === inFilterField.identifier) {
                found = this.checkFieldValue(inField, inFilterField);
            }
        }

        return found;
    }

    /**
     * Method to check for each ds if it has alle selected fitlers inside
     * to have a AND wise fitler
     * @param inDataSets
     */
    private filterAndWise(inDataSets: DataSet[], inFilter: DexieFilter): DataSet[] {
        const andFiltered: DataSet[] = [];

        for (const dataSet of inDataSets) {
            const findMetaData = inFilter.data.filter(f => ApiIdentifers.filterSortByDataSetAll.some(x => x === f.identifier));
            const findFieldData = inFilter.data.filter(f => !ApiIdentifers.filterSortByDataSetAll.some(x => x === f.identifier)).map(f => f.identifier);

            /**
             * first check dataSet meta fields
             */
            for (let index = findMetaData.length - 1; index >= 0; index--) {
                const filterMeta = findMetaData[index];

                switch (filterMeta.identifier) {
                    case ApiIdentifers.filterCreatedAt:
                        if (this.compareDates(dataSet.created.date, filterMeta.valueString || '') === 0) {
                            findMetaData.pop();
                        }
                        break;
                    case ApiIdentifers.filterCreatedBy:
                        if (
                            dataSet.created.userEmail.toLowerCase().includes((filterMeta.valueString || '').toLowerCase()) ||
                            dataSet.created.userName.toLowerCase().includes((filterMeta.valueString || '').toLowerCase())
                        ) {
                            findMetaData.pop();
                        }
                        break;
                    case ApiIdentifers.filterEditedAt:
                        if (dataSet.lastModified && this.compareDates(dataSet.lastModified.date, filterMeta.valueString || '') === 0) {
                            findMetaData.pop();
                        }
                        break;
                    case ApiIdentifers.filterEditedBy:
                        if (
                            dataSet.lastModified &&
                            (dataSet.lastModified.userEmail.toLowerCase().includes((filterMeta.valueString || '').toLowerCase()) ||
                                dataSet.lastModified.userName.toLowerCase().includes((filterMeta.valueString || '').toLowerCase()))
                        ) {
                            findMetaData.pop();
                        }
                        break;
                    case ApiIdentifers.filterDatasetsByMe:
                        let userEmail = inFilter.createdBy;
                        const userName = UserProfileStore.GetUserProfile.userName;
                        const userId = UserProfileStore.GetUserProfile.userId;
                        if (userEmail === '') {
                            userEmail = UserProfileStore.GetUserProfile.userEmail || '';
                        }
                        if (
                            dataSet.created.userId === userId ||
                            dataSet.created.userEmail.toLowerCase().includes(userEmail.toLowerCase()) ||
                            dataSet.created.userName.toLowerCase().includes((userName || '').toLowerCase())
                        ) {
                            findMetaData.pop();
                        }
                        break;
                    case ApiIdentifers.filterTabId:
                        if (dataSet.dataSetId === filterMeta.valueString) {
                            findMetaData.pop();
                        }
                        break;

                    default:
                        break;
                }
            }

            /**
             * second check section fields
             */
            for (const section of dataSet.sections) {
                for (const field of section.fields) {
                    this.filterAndWiseCheckDSField(field, inFilter, findFieldData);
                }
            }
            if (findFieldData.length === 0 && findMetaData.length === 0) {
                /**
                 * If to find lists are empty, the dataSet
                 * contains all search filter fields
                 */
                andFiltered.push(dataSet);
            }
        }

        return andFiltered;
    }

    private filterAndWiseCheckDSField(inField: DataSetField, inFilter: DexieFilter, inToFind: string[]): void {
        if (inField.fields != null) {
            for (const subField of inField.fields) {
                this.filterAndWiseCheckDSField(subField, inFilter, inToFind);
            }
        } else {
            const filterFound = inFilter.data.find(filter => filter.identifier === inField.identifier);
            if (filterFound && this.checkFieldValue(inField, filterFound)) {
                inToFind.splice(inToFind.indexOf(filterFound.identifier), 1);
            }
        }
    }

    private checkFieldValue(inField: DataSetField, inFilterField: DexieFilterField): boolean {
        let found = false;

        if (
            inFilterField.valueAXStrings != null &&
            inFilterField.valueAXStrings.some(
                ax =>
                    inField.valueString &&
                    // use trim and lower case since we only have strings here...
                    (inField.valueString
                        .trim()
                        .toLocaleLowerCase()
                        .includes(
                            ax.value
                                .toString()
                                .trim()
                                .toLocaleLowerCase()
                        ) ||
                        ax.value
                            .toString()
                            .trim()
                            .toLocaleLowerCase()
                            .includes(inField.valueString.trim().toLocaleLowerCase()))
            )
        ) {
            // check for ax select values
            found = true;
        } else if (this.getFieldTypeFromConfiguration(inField) === FieldTypeEnum.SELECT) {
            if (inFilterField.valueNumber != null && inFilterField.valueNumberTo == null && inField.valueNumber != null && inFilterField.valueNumber.some(x => inField.valueNumber === x)) {
                // check for select fields
                found = true;
            }
        } else if (
            inFilterField.valueNumber != null &&
            inFilterField.valueNumberTo != null &&
            inField.valueNumber != null &&
            inField.valueNumber >= inFilterField.valueNumber[0] &&
            inField.valueNumber <= inFilterField.valueNumberTo
        ) {
            // check for number range
            found = true;
        } else if (
            inFilterField.valueNumber != null &&
            inFilterField.valueNumberTo == null &&
            inField.valueNumber != null &&
            inFilterField.valueNumber.some(x => inField.valueNumber && inField.valueNumber >= x)
        ) {
            // check number field values greater or equals than
            found = true;
        } else if (
            (inFilterField.valueNumber == null || isNaN(inFilterField.valueNumber[0])) &&
            inFilterField.valueNumberTo != null &&
            inField.valueNumber != null &&
            inField.valueNumber <= inFilterField.valueNumberTo
        ) {
            // check number field values lower or equals than
            found = true;
        } else if (
            inFilterField.valueString != null &&
            inFilterField.valueStringTo != null &&
            inField.valueString != null &&
            this.compareDates(inField.valueString, inFilterField.valueString) >= 0 &&
            this.compareDates(inField.valueString, inFilterField.valueStringTo) <= 0
        ) {
            // check date range
            found = true;
        } else if (inFilterField.valueString != null && inFilterField.valueStringTo == null && inField.valueString != null) {
            // check string or date
            const checkDate = new Date(inField.valueString);
            if (checkDate instanceof Date && !isNaN(checkDate.getTime())) {
                // handle as date
                found = this.compareDates(inFilterField.valueString, inField.valueString) === 0;
            } else {
                // handle as string compare
                found = inField.valueString.includes(inFilterField.valueString);
            }
        } else if (inFilterField.valueBool != null && inField.valueBool != null && inFilterField.valueBool === inField.valueBool) {
            // check bool value
            found = true;
        }
        return found;
    }

    private getFieldTypeFromConfiguration(inField: DataSetField): FieldTypeEnum {
        const allConfigs = ConfigurationService.GetCompleteConfigurationSections;
        let type = FieldTypeEnum.UNKNOWN;
        for (const section of allConfigs) {
            for (const field of section.fields) {
                type = this.getFieldTypeFromConfigurationField(field, inField);
                if (type !== FieldTypeEnum.UNKNOWN) {
                    break;
                }
            }
            if (type !== FieldTypeEnum.UNKNOWN) {
                break;
            }
        }

        return type;
    }

    private getFieldTypeFromConfigurationField(inConfigField: ConfigurationField, inFieldToFind: DataSetField): FieldTypeEnum {
        let type = FieldTypeEnum.UNKNOWN;
        if (inConfigField.fields != null) {
            for (const subField of inConfigField.fields) {
                type = this.getFieldTypeFromConfigurationField(subField, inFieldToFind);
                if (type !== FieldTypeEnum.UNKNOWN) {
                    break;
                }
            }
        } else if (inConfigField.identifier === inFieldToFind.identifier) {
            type = inConfigField.type;
        }
        return type;
    }

    /**
     * Method to fix an issue with doubled identifier on local saved datasets
     * See also: https://appsfactory.atlassian.net/browse/TTDB-874
     */
    private async fixTBlenMaterialComponentUuid(): Promise<void> {
        await this.GetAllDataSetsFromDexieDB()
            .then(data => {
                if (data && data.length > 0) {
                    const errors: string[] = [];
                    data.forEach(ds => {
                        const sectionBlowRoom = ds.sections.find(s => s.identifier === ApiIdentifers.blowRoom);
                        if (sectionBlowRoom) {
                            const tBlend = sectionBlowRoom.fields.find(f => f.identifier === ApiIdentifers.tBlend);
                            if (tBlend && tBlend.fields) {
                                const feeder = tBlend.fields.find(tf => tf.identifier === ApiIdentifers.BLBOBLHFBLPF);
                                if (feeder && feeder.fields) {
                                    const materialComponent = feeder.fields.find(ff => ff.identifier === ApiIdentifers.materialComponent);
                                    if (materialComponent) {
                                        materialComponent.identifier = ApiIdentifers.tBlendMaterialComponent;
                                        this.PutDataSet(ds)
                                            .then(() => {
                                                LoggerService.debug(`Update T-Blend material component identifier for ds: ${ds.id}`);
                                            })
                                            .catch(error => {
                                                LoggerService.error(`Error on update T-Blend material component identifier for ds: ${ds.id}`, error);
                                                errors.push(`Error on update T-Blend material component identifier for ds: ${ds.id}`);
                                            });
                                    }
                                }
                            }
                        }
                    });
                    if (errors.length > 0) {
                        LoggerService.error(`Could not update/save all datasets: `, errors);
                    }
                    AppSettingsService.SetTBlenMatrialUuidChanged(true);
                }
            })
            .catch(error => {
                LoggerService.LogError(`Error fixTBlenMaterialComponentUuid for local datasets: `, error);
            });
    }
}

export default DataSetProvider;
